// CampaignScheduler.jsx — step builder for campaigns
// Each step has: channel · template · landing page · offset · send time · note
// Template selected here → no separate "Step 5 Message & Link" needed
import React from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { FaPlus, FaTrash, FaGripVertical, FaArrowDown } from "react-icons/fa";

const C = {
  brand:"#4f46e5", brandLt:"#eef2ff",
  green:"#25d366", greenBg:"#f0fdf4",
  text:"#111827", muted:"#6b7280", subtle:"#9ca3af",
  border:"#e5e7eb", bg:"#f9fafb", white:"#ffffff",
  danger:"#ef4444",
};

const CHANNEL_COLOR = { WhatsApp:"#25d366", SMS:"#6c757d", Email:"#0d6efd" };
const CHANNEL_BG    = { WhatsApp:"#f0fdf4", SMS:"#f9fafb",  Email:"#eff6ff" };

const offsetLabel = (d) => {
  if (d === 0)  return "on event day";
  if (d === -1) return "day before";
  if (d === 1)  return "day after";
  return d < 0 ? `${Math.abs(d)} days before` : `${d} days after`;
};

const sendDateTime = (step, eventDate) => {
  if (!eventDate) return `${offsetLabel(step.offsetDays)} at ${step.sendTime}`;
  const d = new Date(eventDate);
  d.setDate(d.getDate() + Number(step.offsetDays));
  const [h, m] = (step.sendTime||"09:00").split(":").map(Number);
  d.setHours(h, m, 0, 0);
  return d.toLocaleString("en-IN", { weekday:"short", day:"numeric", month:"short",
    hour:"2-digit", minute:"2-digit" });
};

const makeStep = () => ({
  id: `cs_${Date.now()}_${Math.random().toString(36).substr(2,4)}`,
  offsetDays: 0,
  sendTime:   "09:00",
  channel:    "WhatsApp",
  templateId: "",
  landingPageId: "",
  note:       "",
});

const Lbl = ({ children }) => (
  <div style={{ fontSize:"0.65rem", fontWeight:800, textTransform:"uppercase",
    letterSpacing:"0.07em", color:C.subtle, marginBottom:4 }}>
    {children}
  </div>
);

const CampaignScheduler = ({ steps, setSteps, templates=[], landingPages=[], eventDate, eventLabel="Event" }) => {
  const addStep    = ()         => setSteps(p => [...p, makeStep()]);
  const removeStep = (id)       => setSteps(p => p.filter(s => s.id !== id));
  const updateStep = (id, k, v) => setSteps(p => p.map(s => s.id === id ? { ...s, [k]: v } : s));

  const onDragEnd = (r) => {
    if (!r.destination) return;
    const arr = [...steps];
    const [m] = arr.splice(r.source.index, 1);
    arr.splice(r.destination.index, 0, m);
    setSteps(arr);
  };

  // When template selected, auto-fill channel + landing page
  const selectTemplate = (stepId, tplId) => {
    updateStep(stepId, "templateId", String(tplId));
    const t = templates.find(x => String(x.templateid || x.id) === String(tplId));
    if (!t) return;
    updateStep(stepId, "channel", t.channel || "WhatsApp");
    const lpId = String(t.landing_page_id || t.landingPageId || "");
    if (lpId) updateStep(stepId, "landingPageId", lpId);
  };

  return (
    <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.border}`,
      overflow:"hidden" }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
        padding:"14px 18px", borderBottom:`1px solid ${C.border}`, background:C.bg }}>
        <div style={{ fontSize:"0.86rem", fontWeight:800, color:C.text }}>
          <i className="fas fa-list-ol" style={{ marginRight:8, color:C.brand, fontSize:"0.78rem" }} />
          Steps
          <span style={{ marginLeft:8, fontSize:"0.72rem", fontWeight:500, color:C.muted }}>
            — each step sends a message on a specific day relative to the event date
          </span>
        </div>
        <button onClick={addStep}
          style={{ display:"inline-flex", alignItems:"center", gap:5,
            padding:"6px 13px", borderRadius:8, border:`1px solid ${C.brand}`,
            background:C.brandLt, color:C.brand, fontSize:"0.78rem",
            fontWeight:700, cursor:"pointer" }}>
          <FaPlus size={10} /> Add Step
        </button>
      </div>

      <div style={{ padding: steps.length ? "16px 18px" : "32px 18px" }}>
        {steps.length === 0 ? (
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:"1.8rem", marginBottom:10 }}>📅</div>
            <div style={{ fontSize:"0.88rem", fontWeight:700, color:C.text, marginBottom:6 }}>
              No steps yet
            </div>
            <p style={{ fontSize:"0.78rem", color:C.muted, marginBottom:14 }}>
              Add a step to define when and how the message is sent.
            </p>
            <button onClick={addStep}
              style={{ padding:"8px 20px", borderRadius:8, border:"none",
                background:C.brand, color:C.white, fontSize:"0.82rem",
                fontWeight:700, cursor:"pointer" }}>
              <FaPlus size={10} style={{ marginRight:6 }} /> Add First Step
            </button>
          </div>
        ) : (
          <>
            {/* Event date anchor */}
            <div style={{ textAlign:"center", marginBottom:8 }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:7,
                padding:"6px 16px", borderRadius:99, background:C.brand,
                color:C.white, fontSize:"0.8rem", fontWeight:700 }}>
                📅 {eventLabel}
                {eventDate && (
                  <span style={{ opacity:0.8, fontSize:"0.75rem" }}>
                    ({new Date(eventDate).toLocaleDateString("en-IN",
                      { day:"numeric", month:"short" })})
                  </span>
                )}
              </span>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="campaign-steps">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {steps.map((step, idx) => {
                      const tpl = templates.find(t => String(t.id) === String(step.templateId));
                      const lp  = landingPages.find(p => String(p.id) === String(step.landingPageId));

                      return (
                        <Draggable key={step.id} draggableId={step.id} index={idx}>
                          {(prov, snap) => (
                            <div ref={prov.innerRef} {...prov.draggableProps}
                              style={{ ...prov.draggableProps.style }}>

                              {/* Connector */}
                              <div style={{ display:"flex", flexDirection:"column",
                                alignItems:"center", margin:"6px 0" }}>
                                <div style={{ width:1, height:14, background:C.border }} />
                                <span style={{ fontSize:"0.66rem", color:C.subtle,
                                  padding:"2px 8px", borderRadius:20,
                                  background:C.bg, border:`1px solid ${C.border}` }}>
                                  {offsetLabel(step.offsetDays)} at {step.sendTime}
                                </span>
                                <div style={{ width:1, height:14, background:C.border }} />
                              </div>

                              {/* Step card */}
                              <div style={{
                                borderRadius:12, overflow:"hidden",
                                border:`1.5px solid ${snap.isDragging ? C.brand : C.border}`,
                                borderLeft:`4px solid ${CHANNEL_COLOR[step.channel]}`,
                                background: snap.isDragging ? C.brandLt : C.white,
                              }}>
                                {/* Step header */}
                                <div style={{ display:"flex", alignItems:"center", gap:8,
                                  padding:"9px 14px", background: CHANNEL_BG[step.channel]||C.bg,
                                  borderBottom:`1px solid ${C.border}` }}>
                                  <div {...prov.dragHandleProps}
                                    style={{ cursor:"grab", color:C.border, flexShrink:0 }}>
                                    <FaGripVertical size={12} />
                                  </div>
                                  <div style={{ width:22, height:22, borderRadius:"50%",
                                    background:CHANNEL_COLOR[step.channel], color:C.white,
                                    fontSize:"0.68rem", fontWeight:800, flexShrink:0,
                                    display:"flex", alignItems:"center", justifyContent:"center" }}>
                                    {idx+1}
                                  </div>
                                  <div style={{ flex:1, fontSize:"0.82rem", fontWeight:700, color:C.text }}>
                                    Step {idx+1}
                                    {tpl && (
                                      <span style={{ fontWeight:400, color:C.muted,
                                        marginLeft:6, fontSize:"0.75rem" }}>
                                        · {tpl.name}
                                      </span>
                                    )}
                                  </div>
                                  {eventDate && (
                                    <span style={{ fontSize:"0.7rem", color:C.muted,
                                      padding:"2px 8px", borderRadius:20,
                                      background:C.white, border:`1px solid ${C.border}` }}>
                                      {sendDateTime(step, eventDate)}
                                    </span>
                                  )}
                                  {steps.length > 1 && (
                                    <button onClick={() => removeStep(step.id)}
                                      style={{ border:"none", background:"none",
                                        color:C.border, cursor:"pointer", padding:"2px 4px",
                                        flexShrink:0 }}
                                      onMouseEnter={e=>{e.currentTarget.style.color=C.danger;}}
                                      onMouseLeave={e=>{e.currentTarget.style.color=C.border;}}>
                                      <FaTrash size={11} />
                                    </button>
                                  )}
                                </div>

                                {/* Step body */}
                                <div style={{ padding:"14px", display:"grid",
                                  gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>

                                  {/* Offset */}
                                  <div>
                                    <Lbl>Offset days</Lbl>
                                    <input type="number" value={step.offsetDays}
                                      min={-30} max={30}
                                      onChange={e=>updateStep(step.id,"offsetDays",parseInt(e.target.value,10)||0)}
                                      style={{ width:"100%", padding:"7px 10px", fontSize:"0.82rem",
                                        border:`1px solid ${C.border}`, borderRadius:8,
                                        outline:"none", color:C.text, textAlign:"center",
                                        fontWeight:700 }} />
                                    <div style={{ fontSize:"0.65rem", color:C.subtle,
                                      textAlign:"center", marginTop:3 }}>
                                      {offsetLabel(step.offsetDays)}
                                    </div>
                                  </div>

                                  {/* Send time */}
                                  <div>
                                    <Lbl>Send time</Lbl>
                                    <input type="time" value={step.sendTime}
                                      onChange={e=>updateStep(step.id,"sendTime",e.target.value)}
                                      style={{ width:"100%", padding:"7px 10px", fontSize:"0.82rem",
                                        border:`1px solid ${C.border}`, borderRadius:8,
                                        outline:"none", color:C.text }} />
                                  </div>

                                  {/* Channel */}
                                  <div>
                                    <Lbl>Channel</Lbl>
                                    <select value={step.channel}
                                      onChange={e=>updateStep(step.id,"channel",e.target.value)}
                                      style={{ width:"100%", padding:"7px 10px", fontSize:"0.82rem",
                                        border:`1px solid ${C.border}`, borderRadius:8,
                                        outline:"none", background:CHANNEL_BG[step.channel]||C.white,
                                        color:C.text, fontWeight:600 }}>
                                      {["WhatsApp","SMS","Email"].map(ch =>
                                        <option key={ch}>{ch}</option>)}
                                    </select>
                                  </div>

                                  {/* Template — full width */}
                                  <div style={{ gridColumn:"1 / span 3" }}>
                                    <Lbl>Message template</Lbl>
                                    {templates.length === 0 ? (
                                      <div style={{ padding:"9px 12px", borderRadius:8,
                                        background:"#fef2f2", border:"1px solid #fecaca",
                                        fontSize:"0.78rem", color:"#dc2626" }}>
                                        No templates yet — go to <strong>Templates</strong> tab to create one
                                      </div>
                                    ) : (
                                      <select value={step.templateId}
                                        onChange={e=>selectTemplate(step.id, e.target.value)}
                                        style={{ width:"100%", padding:"8px 11px", fontSize:"0.82rem",
                                          border:`1px solid ${step.templateId ? C.brand : C.border}`,
                                          borderRadius:8, outline:"none", color:C.text,
                                          background:C.white }}>
                                        <option value="">— Select a message template —</option>
                                        {templates
                                          .filter(t => !step.channel || t.channel === step.channel)
                                          .map(t => (
                                            <option key={t.id} value={String(t.id)}>
                                              {t.name} ({t.channel})
                                            </option>
                                          ))}
                                      </select>
                                    )}
                                  </div>

                                  {/* Landing page — full width */}
                                  <div style={{ gridColumn:"1 / span 3" }}>
                                    <Lbl>Offer page / landing page (optional)</Lbl>
                                    <div style={{ fontSize:"0.72rem", color:C.muted, marginBottom:5 }}>
                                      Replaces <code style={{ background:"#f0f4ff",
                                        padding:"1px 4px", borderRadius:3, color:C.brand }}>
                                        {"{{link}}"}</code> in the template with a tracked offer link.
                                    </div>
                                    {landingPages.length === 0 ? (
                                      <div style={{ padding:"9px 12px", borderRadius:8,
                                        background:"#fffbeb", border:"1px solid #fde68a",
                                        fontSize:"0.78rem", color:"#d97706" }}>
                                        No offer pages yet — go to <strong>Offer Pages</strong> to create one
                                      </div>
                                    ) : (
                                      <select value={step.landingPageId}
                                        onChange={e=>updateStep(step.id,"landingPageId",e.target.value)}
                                        style={{ width:"100%", padding:"8px 11px", fontSize:"0.82rem",
                                          border:`1px solid ${C.border}`, borderRadius:8,
                                          outline:"none", color:C.text, background:C.white }}>
                                        <option value="">None — no offer link</option>
                                        {landingPages.map(p => (
                                          <option key={p.id} value={String(p.id)}>{p.title}</option>
                                        ))}
                                      </select>
                                    )}
                                    {lp && (
                                      <div style={{ marginTop:6, padding:"7px 10px",
                                        borderRadius:7, background:"#f0fdf4",
                                        border:"1px solid #bbf7d0", fontSize:"0.74rem",
                                        color:"#059669", display:"flex",
                                        alignItems:"center", gap:6 }}>
                                        <i className="fas fa-check-circle" />
                                        <strong>{lp.title}</strong>
                                        <span style={{ color:"#9ca3af" }}>·</span>
                                        hotel.co/o/{lp.slug}
                                        <span style={{ color:"#9ca3af" }}>·</span>
                                        {lp.status}
                                      </div>
                                    )}
                                  </div>

                                  {/* Note — full width */}
                                  <div style={{ gridColumn:"1 / span 3" }}>
                                    <Lbl>Internal note (optional)</Lbl>
                                    <input value={step.note||""}
                                      onChange={e=>updateStep(step.id,"note",e.target.value)}
                                      placeholder="e.g. Birthday promo — WhatsApp"
                                      style={{ width:"100%", padding:"7px 10px", fontSize:"0.8rem",
                                        border:`1px solid ${C.border}`, borderRadius:8,
                                        outline:"none", color:C.text, background:C.white,
                                        boxSizing:"border-box" }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {/* End node */}
            <div style={{ display:"flex", flexDirection:"column",
              alignItems:"center", marginTop:8, gap:0 }}>
              <div style={{ width:1, height:16, background:C.border }} />
              <span style={{ padding:"6px 18px", borderRadius:99, fontSize:"0.78rem",
                color:C.muted, border:`1px solid ${C.border}`,
                background:C.bg, fontWeight:600 }}>
                ✅ Campaign ends
              </span>
            </div>

            {/* Add step button */}
            <div style={{ textAlign:"center", marginTop:16 }}>
              <button onClick={addStep}
                style={{ padding:"7px 20px", borderRadius:8,
                  border:`2px dashed ${C.border}`, background:"none",
                  color:C.muted, fontSize:"0.8rem", fontWeight:600, cursor:"pointer" }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=C.brand;e.currentTarget.style.color=C.brand;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.muted;}}>
                <FaPlus size={10} style={{ marginRight:5 }} /> Add another step
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CampaignScheduler;
